<template>
    <div class="wall-page">
        <!-- 背景动效层 -->
        <div class="bg-grid"></div>
        <div class="scanline"></div>

        <div class="wall-toolbar">
            <div class="left">
                <el-input v-model="search.keyword" size="large" placeholder="按名称 / URL 搜索" clearable class="kw"
                    @keyup.enter="doSearch">
                    <template #prefix><el-icon>
                            <Search />
                        </el-icon></template>
                </el-input>

                <el-button type="primary" size="large" @click="doSearch">
                    <el-icon>
                        <Search />
                    </el-icon> 搜索
                </el-button>

                <!-- 全选本页（带半选） -->
                <el-checkbox v-model="allChecked" :indeterminate="indeterminate" class="ml10" @change="toggleAll">
                    全选本页
                </el-checkbox>

                <!-- 批量删除 -->
                <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 个视频吗？`"
                    @confirm="doBatchDelete">
                    <template #reference>
                        <el-button type="danger" size="large" :disabled="!selectedIds.length" :loading="batching">
                            批量删除<span v-if="selectedIds.length">（{{ selectedIds.length }}）</span>
                        </el-button>
                    </template>
                </el-popconfirm>
            </div>

            <div class="right">

                <el-button class="mr8"
                    :disabled="!selectedIds.length || !list.some(r => selectedIds.includes(r.id) && r.video_url)"
                    :loading="zipMaking" @click="downloadBatchZip">
                    <el-icon>
                        <Download />
                    </el-icon>
                    批量打包下载ZIP

                </el-button>

                <el-button size="large" :loading="loading" @click="fetchList">
                    <el-icon>
                        <Refresh />
                    </el-icon> 刷新
                </el-button>
            </div>
        </div>

        <!-- 骨架屏 -->
        <transition-group name="fade" tag="div" class="grid" v-if="loading">
            <div v-for="n in pageSize" :key="'skeleton-' + n" class="card skeleton">
                <div class="thumb shimmer"></div>
                <div class="meta">
                    <div class="line shimmer"></div>
                    <div class="line short shimmer"></div>
                </div>
            </div>
        </transition-group>

        <!-- 视频卡片（竖版 9:16） -->
        <transition-group name="stagger" tag="div" class="grid" v-else>
            <div v-for="it in list" :key="it.id" :class="['card', 'glow', { selected: checkedMap[it.id] }]"
                @mouseenter="hoverId = it.id" @mouseleave="hoverId = null">
                <el-checkbox v-model="checkedMap[it.id]" class="checker" @change="onCheck(it)" />
                <video class="thumb portrait" :src="it.video_url" :title="it.video_name || '未命名视频'" preload="metadata"
                    playsinline controls :muted="true"></video>

                <div class="meta">
                    <div class="name" :title="it.video_name || ''">{{ it.video_name || '未命名视频' }}</div>
                    <div class="row">
                        <el-tag size="small">使用 {{ it.use_num || 0 }}</el-tag>
                        <span class="time">{{ it.create_time }}</span>
                    </div>
                </div>

                <div class="actions" v-show="hoverId === it.id">
                    <el-popconfirm title="确认删除该视频吗？" @confirm="delOne(it.id)">
                        <template #reference>
                            <el-button type="danger" size="small">
                                <el-icon>
                                    <Delete />
                                </el-icon> 删除
                            </el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </div>
        </transition-group>

        <!-- 分页 -->
        <div class="pager">
            <el-pagination v-model:current-page="page" v-model:page-size="pageSize" :total="total"
                :page-sizes="[12, 24, 36, 60]" layout="total, sizes, prev, pager, next, jumper" @size-change="fetchList"
                @current-change="fetchList" />
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '@/config.js';
import { saveAs } from 'file-saver'
import { Search, Refresh, Delete } from '@element-plus/icons-vue';

export default {
    name: 'AllVideosWall',
    components: { Search, Refresh, Delete },
    data() {
        return {
            loading: false,
            batching: false,
            list: [],
            total: 0,
            page: 1,
            pageSize: 10,
            search: { keyword: '' },
            zipMaking: false,
            zipProgress: 0,
            checkedMap: {},
            selectedIds: [],
            allChecked: false,
            indeterminate: false,

            hoverId: null,

            // 最小骨架展示时长（更有“加载中”感觉）
            minSkeletonMs: 800,
        }
    },
    computed: {
        userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
        api() { return `${config.BASE_URL}/shop/api/video_cp_materials.php` }, // 如需切到 sc 表，这里改文件名
    },
    mounted() { this.fetchList(); },
    methods: {
        async fetchList() {
            const start = Date.now();
            try {
                this.loading = true;
                const payload = {
                    action: this.search.keyword ? 'search_all' : 'list_all',
                    store_id: this.userInfo.id,
                    page: this.page,
                    page_size: this.pageSize
                };
                if (this.search.keyword) payload.keyword = this.search.keyword;

                const { data } = await axios.post(this.api, payload);
                this.list = data?.data || [];
                this.total = data?.pagination?.total || 0;

                // 重置选择
                this.checkedMap = {};
                this.selectedIds = [];
                this.syncCheckState();
            } catch (e) {
                this.$message.error('加载失败');
                console.error(e);
            } finally {
                const elapsed = Date.now() - start;
                const remain = Math.max(0, this.minSkeletonMs - elapsed);
                setTimeout(() => { this.loading = false; }, remain);
            }
        },
        doSearch() {
            this.page = 1;
            this.fetchList();
        },
        speakerLabelByIndex(i) {
            if (!this.speakers?.length || !this.form.voice_clone_id?.length) return '未选择'
            const vid = this.form.voice_clone_id[i % this.form.voice_clone_id.length]
            const found = this.speakers.find(s => s.voice_id === vid)
            return found ? `${found.voice_name}（${found.voice_id}）` : vid
        },
        async downloadBatchZip() {
            const rows = this.list
                .filter(r => this.selectedIds.includes(r.id))
                .filter(r => r.video_url)

            if (!rows.length) {
                this.$message.warning('请选择至少一个“已合成”的视频')
                return
            }

            this.zipMaking = true
            try {
                const items = rows.map(r => ({
                    url: r.video_url,
                    filename: this.buildSafeName(`${r.video_name || 'video'}_${r.id}.mp4`)
                }))

                const res = await fetch(`${config.BASE_URL}/shop/api/zip_videos.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items })
                })

                if (!res.ok) throw new Error(`HTTP ${res.status}`)

                const blob = await res.blob()
                saveAs(blob, `videos_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.zip`)
                this.$message.success(`打包完成，共 ${rows.length} 个视频`)
            } catch (e) {
                console.error(e)
                this.$message.error('打包失败，请重试')
            } finally {
                this.zipMaking = false
                this.zipProgress = 0
            }
        },


        // 抓取远端文件为 Blob（注意：若视频URL不允许跨域，将报错）
        async fetchAsBlob(url) {
            // 用 fetch 保留文件名/类型，且能拿到 blob
            const res = await fetch(url, { mode: 'cors' }) // 如跨域受限，可改方案B走后端
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            return await res.blob()
        },

        // 简单的文件名清洗
        buildSafeName(name) {
            return String(name)
                .replace(/[\\/:*?"<>|]/g, '_')
                .replace(/\s+/g, '_')
                .slice(0, 120) // 防止过长
        },

        // 单个下载（之前的普通下载仍可保留）
        downloadOne(row) {
            if (!row?.video_url) {
                this.$message.warning('没有可下载的视频')
                return
            }
            this.forceDownload(row.video_url, `${row.video_name || 'video'}_${row.id}.mp4`)
        },

        forceDownload(url, filename) {
            const a = document.createElement('a')
            a.href = url
            a.download = filename || 'video.mp4'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        },
        onCheck(it) {
            const on = !!this.checkedMap[it.id];
            if (on && !this.selectedIds.includes(it.id)) {
                this.selectedIds.push(it.id);
            } else if (!on) {
                this.selectedIds = this.selectedIds.filter(id => id !== it.id);
            }
            this.syncCheckState();
        },

        toggleAll(val) {
            if (!Array.isArray(this.list) || !this.list.length) return;

            // 一次性构造新的勾选映射，触发响应式
            this.checkedMap = val
                ? Object.fromEntries(this.list.map(it => [it.id, true]))
                : {};

            // 同步选中 ID 集合
            this.selectedIds = val ? this.list.map(it => it.id) : [];

            this.syncCheckState();
        },


        syncCheckState() {
            const total = this.list.length;
            const checkedCount = this.list.filter(it => this.checkedMap[it.id]).length;
            this.allChecked = total > 0 && checkedCount === total;
            this.indeterminate = checkedCount > 0 && checkedCount < total;
        },

        async delOne(id) {
            try {
                const { data } = await axios.post(this.api, {
                    action: 'delete',
                    id,
                    store_id: this.userInfo.id
                });
                if (data?.success) {
                    this.$message.success('已删除');
                    this.fetchList();
                } else {
                    this.$message.error(data?.error || '删除失败');
                }
            } catch (e) {
                this.$message.error('删除失败');
            }
        },

        async doBatchDelete() {
            if (!this.selectedIds.length) return;
            try {
                this.batching = true;
                const { data } = await axios.post(this.api, {
                    action: 'batch_delete',
                    store_id: this.userInfo.id,
                    ids: this.selectedIds
                });
                if (data?.success) {
                    this.$message.success(`批量删除成功（${data.deleted || this.selectedIds.length}）`);
                    this.fetchList();
                } else {
                    this.$message.error(data?.error || '批量删除失败');
                }
            } catch (e) {
                this.$message.error('批量删除失败');
            } finally {
                this.batching = false;
            }
        }
    }
}
</script>

<style scoped lang="scss">
.wall-page {
    position: relative;
    padding: 12px 16px;
    overflow: hidden;
}

/* 背景科幻网格 */
.bg-grid {
    position: absolute;
    inset: -20vmax;
    z-index: -2;
    background:
        radial-gradient(ellipse at top, rgba(36, 83, 255, .25), transparent 60%),
        radial-gradient(ellipse at bottom, rgba(0, 255, 224, .12), transparent 60%),
        repeating-linear-gradient(0deg, rgba(255, 255, 255, .05) 0 1px, transparent 1px 60px),
        repeating-linear-gradient(90deg, rgba(255, 255, 255, .04) 0 1px, transparent 1px 60px);
    filter: blur(2px);
    animation: drift 22s linear infinite;
}

@keyframes drift {
    0% {
        transform: translate3d(0, 0, 0) rotate(0deg);
    }

    50% {
        transform: translate3d(10px, -6px, 0) rotate(0.5deg);
    }

    100% {
        transform: translate3d(0, 0, 0) rotate(0deg);
    }
}

/* 扫描线叠加层 */
.scanline {
    pointer-events: none;
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(transparent 50%, rgba(0, 0, 0, .15) 51%),
        linear-gradient(90deg, rgba(32, 124, 255, .05), rgba(0, 255, 204, .05));
    background-size: 100% 4px, 100% 100%;
    mix-blend-mode: overlay;
    animation: scan 3s linear infinite;
}

@keyframes scan {
    0% {
        background-position: 0 0, 0 0;
    }

    100% {
        background-position: 0 4px, 0 0;
    }
}

.wall-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .left {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .right {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .kw {
        width: 360px;
    }

    .ml10 {
        margin-left: 10px;
    }
}

.grid {
    display: grid;
    /* 竖版卡片更窄，展示更多列 */
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 18px;
}

/* 卡片 */
.card {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    background: #0b1320;
    border: 1px solid rgba(120, 170, 255, .22);
    box-shadow: 0 10px 22px rgba(6, 18, 36, .25);
    transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease, filter .25s ease;
    transform: translateZ(0);

    &:hover {
        transform: translateY(-3px) scale(1.01);
        box-shadow: 0 18px 38px rgba(6, 18, 36, .38);
        border-color: rgba(120, 170, 255, .38);
    }
}

/* 选中态更亮 */
.selected {
    border-color: rgba(0, 255, 204, .7) !important;
    box-shadow: 0 0 24px rgba(0, 255, 204, .25);
}

/* 霓虹脉冲（不断有新内容感） */
.glow {
    animation: glowPulse 3.2s ease-in-out infinite;
}

@keyframes glowPulse {
    0% {
        box-shadow: 0 0 0 rgba(80, 149, 255, 0);
    }

    50% {
        box-shadow: 0 0 26px rgba(80, 149, 255, .18);
    }

    100% {
        box-shadow: 0 0 0 rgba(80, 149, 255, 0);
    }
}

/* 勾选器 */
.checker {
    position: absolute;
    left: 12px;
    top: 12px;
    z-index: 2;

    :deep(.el-checkbox__inner) {
        transform: scale(1.05);
    }
}

/* 竖版预览：9:16，沉浸更大 */
.thumb {
    width: 100%;
    /* 默认横版用 16/9；此处我们强制竖版 9/16 */
    aspect-ratio: 9 / 16;
    height: auto;
    background: #000;
    object-fit: cover;
    /* 竖版素材与容器等比填充，更满更沉浸 */
    transform: translateZ(0);
    transition: transform .25s ease, filter .25s ease;
}

.card:hover .thumb {
    transform: scale(1.02);
    filter: saturate(1.05);
}

/* 信息区 */
.meta {
    padding: 12px 14px 14px;
    color: #cdd6e4;

    .name {
        font-weight: 700;
        letter-spacing: .2px;
        color: #e6eefb;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 8px 0 2px;
    }

    .time {
        font-size: 12px;
        opacity: .7;
    }
}

/* 悬浮操作 */
.actions {
    position: absolute;
    right: 12px;
    top: 12px;
    z-index: 2;
}

/* 骨架屏（竖版高宽比） */
.skeleton {
    .thumb {
        aspect-ratio: 9 / 16;
        border-bottom: 1px solid rgba(255, 255, 255, .06);
    }

    .meta {
        padding: 14px;
    }

    .line {
        height: 14px;
        border-radius: 6px;
        background: rgba(255, 255, 255, .08);
        margin-bottom: 10px;
    }

    .short {
        width: 60%;
    }
}

.shimmer {
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, .12) 50%, rgba(255, 255, 255, 0) 100%);
        animation: shimmer 1.6s infinite;
    }
}

@keyframes shimmer {
    100% {
        transform: translateX(100%);
    }
}

/* 过渡 */
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity .25s ease;
}

.stagger-enter-from {
    opacity: 0;
    transform: translateY(8px) scale(.98);
}

.stagger-enter-active {
    transition: all .28s ease;
}

/* 分页 */
.pager {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
}
</style>
